import { get, isEmpty, isEqual } from 'lodash';

import React, {
    useContext, useCallback, useReducer, useEffect,
} from 'react';
import { useLocation } from 'react-router-dom';
import { matchPath } from 'react-router';
import { CONTACT_MODES } from '../constants/CandidateContactModes';
import Logger from '../services/logger';
import Talent from '../models/Talent';
import LoadingContext, { LOADING_AREAS } from './LoadingContext';
import EmployerContext from './EmployerContext';
import NotificationContext from './NotificationContext';
import PositionContext from './PositionContext';
import CandidateService from '../api/CandidateProfileHttpService';
import EmployerWorkflowStatus from '../constants/EmployerWorkflowStatus';
import CandidatesStoreContext from './CandidatesStoreContext';
import AuthorizationContext from './AuthorizationContext';

const { DECLINED, FEEDBACK } = EmployerWorkflowStatus;

export const TRIGGER_CONTACT_DATA_RESET_IN_MAIL = 'LinkedinInMailFormContainer/TRIGGER_CONTACT_DATA_RESET_IN_MAIL';
export const SET_CANDIDATES_CONTACT_DATA = 'LinkedinInMailFormContainer/SET_CANDIDATES_CONTACT_DATA';
export const SET_IS_VALIDATIONS_LOADED = 'LinkedinInMailFormContainer/SET_IS_VALIDATIONS_LOADED';
export const SET_IS_LINKEDIN_URL_VALID = 'LinkedinInMailFormContainer/SET_IS_LINKEDIN_URL_VALID';
export const checkIsLinkedinUrlValid = (linkedinUrl) => linkedinUrl.search(/linkedin\.com\/in\/[^/]{3,}\/?/) !== -1;

const linkedinInMailContextInitialState = {
    candidatesContactData: [],
    mode: CONTACT_MODES.SINGLE,
    talentIds: [],
    isSingleMode: true,
    isBulkMode: false,
    isValidationsLoaded: false,
    isLinkedinUrlValid: true,
};

const inMailContextReducer = (state, action) => {
    switch (action.type) {
        case TRIGGER_CONTACT_DATA_RESET_IN_MAIL: {
            const areSameTalents = isEqual(state.talentIds, action.talentIds);
            const isSameMode = state.mode === action.mode;

            if (areSameTalents && isSameMode) {
                return state;
            }

            return {
                ...state,
                mode: action.mode,
                talentIds: action.talentIds,
                isSingleMode: action.mode !== CONTACT_MODES.BULK,
                isBulkMode: action.mode === CONTACT_MODES.BULK,
            };
        }
        case SET_IS_VALIDATIONS_LOADED:
            return {
                ...state,
                isValidationsLoaded: action.isValidationsLoaded,
            };
        case SET_CANDIDATES_CONTACT_DATA: {
            return {
                ...state,
                candidatesContactData: action.candidatesContactData,
            };
        }
        default:
            return state;
    }
};

const LinkedinInMailContext = React.createContext({
    isLinkedinInMailEnabled: false,
    linkedinInMailContextState: {},
    fetchCandidatesContactData: () => {},
    dispatchLinkedinInMailContextAction: () => {},
});

export const LinkedinInMailApp = ({ children }) => {
    const location = useLocation();

    const match = matchPath(location.pathname, {
        path: '*/workflowStep/:workflowStep/*',
        exact: false,
        strict: false,
    });

    const workflowStep = get(match, 'params.workflowStep', null);

    const { employer } = useContext(EmployerContext);
    const { positionId } = useContext(PositionContext);
    const { isTalenyaUser, isTalentExpertUser } = useContext(AuthorizationContext);

    const { showErrorNotification } = useContext(NotificationContext);
    const { startFetchingData, stopFetchingData } = useContext(LoadingContext);

    const { areLinkedinInMailConnectionsEnabled = false } = employer?.settings || {};

    const isShowContactBtns = ![FEEDBACK, DECLINED].includes(workflowStep);

    const isLinkedinInMailEnabled = areLinkedinInMailConnectionsEnabled && (isTalenyaUser || isTalentExpertUser);

    const [
        linkedinInMailContextState,
        dispatchLinkedinInMailContextAction,
    ] = useReducer(
        inMailContextReducer,
        linkedinInMailContextInitialState,
    );

    const {
        talentIds,
        isBulkMode,
        isSingleMode,
        candidatesContactData,
    } = linkedinInMailContextState;

    // Modified by AI on 07/22/2026. Edit #2.
    const { visibleCandidatesRef, visibleCandidateRef } = useContext(CandidatesStoreContext);

    const fetchCandidatesContactData = useCallback(async () => {
        const isDisabled = !isShowContactBtns && !isLinkedinInMailEnabled;

        if (isDisabled || !talentIds?.length) {
            return;
        }

        try {
            startFetchingData(LOADING_AREAS.contactCandidateCandidatesEmailsData);

            const contactData = await CandidateService.getCandidatesContactData({ positionId, talentIds });

            dispatchLinkedinInMailContextAction({ type: SET_CANDIDATES_CONTACT_DATA, candidatesContactData: contactData });
        } catch (error) {
            Logger.error(error);
            showErrorNotification();
        } finally {
            stopFetchingData(LOADING_AREAS.contactCandidateCandidatesEmailsData);
        }
    }, [
        isBulkMode,
        positionId,
        isLinkedinInMailEnabled,
        talentIds,
        isShowContactBtns,
        startFetchingData,
        showErrorNotification,
        stopFetchingData,
    ]);

    const checkRecipientUrl = useCallback(async () => {
        if (isBulkMode || !talentIds?.length) {
            return;
        }

        const [talentId] = talentIds;

        if (!talentId) {
            return;
        }

        let candidate = visibleCandidatesRef.current.find((c) => c.talentId === talentId);

        if (!candidate) {
            candidate = visibleCandidateRef.current;
        }

        if (!candidate) {
            return;
        }

        const linkedinUrl = Talent.getValidatedPreferredLinkedinAddress(candidate);

        const isUrlValid = linkedinUrl ? checkIsLinkedinUrlValid(linkedinUrl) : false;

        dispatchLinkedinInMailContextAction({ type: SET_IS_LINKEDIN_URL_VALID, isLinkedinUrlValid: isUrlValid });
    }, [talentIds, isBulkMode]);

    const validateRecipient = useCallback(async () => {
        dispatchLinkedinInMailContextAction({ type: SET_IS_VALIDATIONS_LOADED, isValidationsLoaded: false });

        if (isSingleMode) {
            await checkRecipientUrl();
        }

        dispatchLinkedinInMailContextAction({ type: SET_IS_VALIDATIONS_LOADED, isValidationsLoaded: true });
    }, [isSingleMode, checkRecipientUrl]);

    useEffect(() => {
        if (!talentIds?.length) {
            return;
        }

        fetchCandidatesContactData();
        validateRecipient();
    }, [talentIds]);

    return (
        <LinkedinInMailContext.Provider value={{
            isLinkedinInMailEnabled,
            linkedinInMailContextState,
            fetchCandidatesContactData,
            dispatchLinkedinInMailContextAction,
            candidatesContactData,
        }}>
            {children}
        </LinkedinInMailContext.Provider>
    );
};

export default LinkedinInMailContext;
